import Api from 'entities/helpers/api';

export const fetchCreatorInfo = async (ids) => {
  const userMap = {};
  const gql = String.raw;
  const query = gql`
    query ($ids: [Int!]) {
      users(ids: $ids) {
        name
        id
      }
    }
  `;

  const creatorInfo = await Api.GRAPHQL.GRAPHQL.post({
    data: JSON.stringify({
      operationName: null,
      query: String.raw`${query}`,
      variables: { ids: ids },
    }),
  });

  creatorInfo.data.users.forEach((creator) => {
    if (creator) {
      const { id, name } = creator;
      userMap[id] = { name };
    }
  });
  return userMap;
};

const mapSourceDataToTarget = (target, source, targetField, newField) => {
  target.forEach((target) => {
    if (target[targetField]) {
      target[newField] = source[target[targetField]];
    }
  });
};

const removeDuplicateTasks = (tasks) => {
  const taskMap = new Set();
  return tasks.reduce((accum, task) => {
    if (!taskMap.has(task.id)) {
      taskMap.add(task.id);
      return [...accum, task];
    }
    return accum;
  }, []);
};

/**
 * Fetch tasks matching the given filters. By default, also performs two extra
 * GraphQL calls to enrich tasks with creator_info and owner_info (each shaped
 * as { name }, keyed by user id). Pass { skipUserEnrichment: true } for callers
 * that only need task fields or aggregate counts — saves two round trips per page.
 */
export const fetchTasksByAppliedFilters = async (taskFilters, { skipUserEnrichment = false } = {}) => {
  const usersSet = new Set();
  const ownersSet = new Set();
  const gql = String.raw;
  const query = gql`
    query (
      $id: BigInt
      $status: String
      $statuses: [String]
      $target_contact_id: BigInt
      $target_contact_ids: [BigInt]
      $creator_user_id: BigInt
      $creator_user_ids: [BigInt]
      $owner_user_id: BigInt
      $owner_user_ids: [BigInt]
      $action_types: [String]
      $cadence_task: Boolean
      $exclude: [JSON]
      $offset: Int
      $limit: Int
      $sort: [String]
    ) {
      tasks(
        id: $id
        status: $status
        statuses: $statuses
        target_contact_id: $target_contact_id
        target_contact_ids: $target_contact_ids
        creator_user_id: $creator_user_id
        creator_user_ids: $creator_user_ids
        owner_user_id: $owner_user_id
        owner_user_ids: $owner_user_ids
        action_types: $action_types
        cadence_task: $cadence_task
        exclude: $exclude
        offset: $offset
        limit: $limit
        sort: $sort
      ) {
        items {
          id
          action_type
          comments
          completed_at
          contact_journey_id
          created_at
          creator_user_id
          deleted_at
          description
          current_due_date
          static_due_date
          journey_task_id
          oid
          owner_user_id
          status
          target_contact_id
          title
          updated_at
          updater_user_id
          cadence_id
          cadence_event_id
          cadence_member_id
          task_source
          metadata {
            journey_id
            journey_name
            step_number
            day_number
          }
          contact {
            contact_attributes {
              name_full
              name_first
              name_last
              name_nick
              name_prefix
            }
            emails {
              email
              primary
              type
            }
            phones {
              phone
              primary
              type
            }
            profile_image {
              url
            }
            id
          }
        }
        total
      }
    }
  `;
  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: taskFilters,
      }),
    });
    const tasksResponse = resp.data.tasks.items;

    tasksResponse.forEach((task) => {
      if (task.creator_user_id) {
        usersSet.add(task.creator_user_id);
      }
      if (task.owner_user_id) {
        ownersSet.add(task.owner_user_id);
      }
      task.completed_at = Date.parse(task.completed_at) / 1000;
    });

    if (!skipUserEnrichment) {
      const creatorInfo = await fetchCreatorInfo(Array.from(usersSet));
      const ownerInfo = await fetchCreatorInfo(Array.from(ownersSet));
      mapSourceDataToTarget(tasksResponse, creatorInfo, 'creator_user_id', 'creator_info');
      mapSourceDataToTarget(tasksResponse, ownerInfo, 'owner_user_id', 'owner_info');
    }

    return { items: removeDuplicateTasks(tasksResponse), total: resp.data.tasks.total };
  } catch (e) {
    throw e;
  }
};

export const fetchAllTasksCount = async (taskFilters) => {
  const gql = String.raw;
  const query = gql`
    query ($user_id: BigInt) {
      allTasksCount(user_id: $user_id) {
        total
      }
    }
  `;

  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: taskFilters,
      }),
    });
    return { total: resp.data.allTasksCount.total };
  } catch (e) {
    throw e;
  }
};

/**
 * Lightweight fetch to get all cadence/journey IDs from a user's tasks.
 * Used to build filter dropdown options without fetching full task details.
 */
export const fetchTaskCadenceIds = async (userId) => {
  const gql = String.raw;
  const query = gql`
    query ($owner_user_id: BigInt, $statuses: [String]) {
      tasks(owner_user_id: $owner_user_id, statuses: $statuses, offset: 0, limit: 10000) {
        items {
          id
          cadence_id
          task_source
          metadata {
            journey_id
            journey_name
          }
        }
        total
      }
    }
  `;
  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: { owner_user_id: userId, statuses: ['IN_PROGRESS', 'COMPLETED'] },
      }),
    });
    const items = resp.data.tasks.items || [];

    // Cadence 2.0 IDs
    const cadence2Ids = new Set();
    // Journey 1.0: id → name map (so we don't need a separate journeys fetch)
    const journey1Map = new Map();

    for (const t of items) {
      if (t.task_source === 'cadence_v2' && t.cadence_id) {
        cadence2Ids.add(String(t.cadence_id));
      }
      if (t.metadata?.journey_id) {
        journey1Map.set(String(t.metadata.journey_id), t.metadata.journey_name || `Journey ${t.metadata.journey_id}`);
      }
    }
    return { cadence2Ids, journey1Map };
  } catch {
    return { cadence2Ids: new Set(), journey1Map: new Map() };
  }
};

export const fetchThankviewEngagements = async (contactId) => {
  const gql = String.raw;
  const query = gql`
    query ($contactId: BigInt!) {
      interactionFindTVEngagementsByContactId(contact_id: $contactId) {
        id
        date_occurred
        custom_fields {
          value
          meta_data {
            display_name
          }
        }
      }
    }
  `;
  try {
    const resp = await Api.GRAPHQL.GRAPHQL.post({
      data: JSON.stringify({
        operationName: null,
        query: String.raw`${query}`,
        variables: { contactId },
      }),
    });
    return resp.data.interactionFindTVEngagementsByContactId;
  } catch (e) {
    throw e;
  }
};
